import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import { connect } from 'react-redux';
import {
  loadMaCisCreator,
  setMaCisPageCreator,
  setMaCisPerPageCreator,
  setMaCisSortCreator,
  setMaCisFiltersCreator,
  removeMaCisFiltersCreator,
  setMaCisProfileCreator,
} from 'store/actionsCreators';
import PropTypes from 'prop-types';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { removeMaCisCreator } from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import moment from 'moment';

const Assessors = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeAssessor,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []);

  useEffect(() => {
    if (document.getElementById('ma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('global.id')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.fullName || ''}
                  onFilter={(val) => handleColumnFilterChange('fullName', val)}
                  filterPlaceholder={t('page.user.cis.assessors.fullNamePlaceholder')}
                  label={t('page.user.cis.assessors.fullName')}
                  sort
                  sortDirection={getSortDirection('fullName')}
                  onSort={(dir) => handleColumnSort('fullName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFromFrom || ''}
                  filterValueTo={filters.dateFromTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFromFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateFromTo', val)}
                  filterPlaceholder={t('page.user.cis.assessors.dateFromPlaceholder')}
                  label={t('page.user.cis.assessors.dateFrom')}
                  labelAlign="center"
                  sort
                  sortDirection={getSortDirection('dateFrom')}
                  onSort={(dir) => handleColumnSort('dateFrom', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.assessors.type')} labelAlign="center" />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.assessors.signature')}
                  labelAlign="center"
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.assessors.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('global.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.fullName')}:</Table.Label>
                      {[item.user.firstName, item.user.middleName, item.user.lastName].join(' ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.dateFrom')}:</Table.Label>
                      {moment(item.dateFrom).format('L')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.type')}:</Table.Label>
                      {item.role?.label || '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.signature')}:</Table.Label>
                      {item.signatureType === 'I' && (
                        <span>{t('page.user.cis.assessors.signatureTypeItalic')}</span>
                      )}
                      {item.signatureType === 'N' && (
                        <span>{t('page.user.cis.assessors.signatureTypeNone')}</span>
                      )}
                      {item.signatureType === 'H' &&
                        (item.hasFileUrl ? t('global.handwritten') : t('global.noPicture'))}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.assessors.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/ma/cis/assessors/edit`);
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.assessors.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeAssessor(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button style={{ width: '30%' }} secondary as={Link} to={`/ma/cis/assessors/create`}>
            {t('page.user.cis.assessors.new')}
          </Button>,
          document.getElementById('ma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

Assessors.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeAssessor: PropTypes.func.isRequired,
};

Assessors.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_MA_CIS'),
  list: ma.cis.assessors,
  countries: globals.countries,
  memberAssociations: globals.availableMA,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadMaCisCreator('assessors')),
  setPage: (payload) => dispatch(setMaCisPageCreator('assessors', payload)),
  setPerPage: (payload) => dispatch(setMaCisPerPageCreator('assessors', payload)),
  setSort: (payload) => dispatch(setMaCisSortCreator('assessors', payload)),
  setFilters: (payload) => dispatch(setMaCisFiltersCreator('assessors', payload)),
  removeFilters: () => dispatch(removeMaCisFiltersCreator('assessors')),
  setProfile: (payload) => dispatch(setMaCisProfileCreator('assessors', payload)),
  removeAssessor: (payload) => dispatch(removeMaCisCreator('assessors', payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Assessors);
